import React from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const InvoiceDetailModal = ({
    visible,
    onHide,
    invoiceDetails,
    isLoading
}) => {
    const { th } = useLanguage();

    const formatCurrency = (amount, currency = 'TRY') => {
        if (amount === null || amount === undefined || isNaN(amount)) {
            return '-';
        }
        // Currency null veya undefined ise default olarak TRY kullan
        const validCurrency = currency || 'TRY';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: validCurrency
        }).format(amount);
    };

    // Get the main item directly from data (not from data.items)
    const mainItem = invoiceDetails?.data || null;

    // Extract detailed lists from the main item - null kontrolü ekle
    const parts = mainItem?.parts || [];
    const labors = mainItem?.labors || [];
    const others = mainItem?.others || [];

    // Calculate totals from the main item - null kontrolü ekle
    const totals = {
        partsTotal: mainItem?.partsTotal || 0,
        laborTotal: mainItem?.laborTotal || 0,
        othersTotal: mainItem?.othersTotal || 0,
        totalClaimAmount: mainItem?.totalClaimAmount || 0
    };

    return (
        <Dialog
            header={th('invoiceApproval.detailModal.title')}
            visible={visible}
            onHide={onHide}
            style={{ width: '900px' }}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            modal
        >
            {isLoading ? (
                <div className="flex justify-center items-center p-4">
                    <ProgressSpinner />
                </div>
            ) : mainItem ? (
                <div className="space-y-4">
                    {/* Basic Information */}
                    <div>
                        <h3 className="font-semibold text-lg mb-2">
                            <i className="pi pi-info-circle mr-2"></i>
                            {th('invoiceApproval.detailModal.basicInfo')}
                        </h3>
                        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-md">
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-hashtag mr-1"></i>
                                    {th('invoiceApproval.detailModal.warrantyClaimNo')}:
                                </label>
                                <p className="mt-1">{mainItem.warrClaimsNo || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-file-text mr-1"></i>
                                    {th('invoiceApproval.detailModal.invoiceNumber')}:
                                </label>
                                <p className="mt-1">{mainItem.invoiceNumber || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-building mr-1"></i>
                                    {th('invoiceApproval.detailModal.dealerName')}:
                                </label>
                                <p className="mt-1">{mainItem.bayiAdi || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-shield mr-1"></i>
                                    {th('invoiceApproval.detailModal.warrantyType')}:
                                </label>
                                <p className="mt-1">{mainItem.warrType || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-car mr-1"></i>
                                    {th('invoiceApproval.detailModal.chassisNo')}:
                                </label>
                                <p className="mt-1">{mainItem.chassisNo || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-tag mr-1"></i>
                                    {th('invoiceApproval.detailModal.productGroup')}:
                                </label>
                                <p className="mt-1">{mainItem.productGroup || '-'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                        <h3 className="font-semibold text-lg mb-2">
                            <i className="pi pi-info-circle mr-2"></i>
                            {th('invoiceApproval.detailModal.additionalInfo')}
                        </h3>
                        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-md">
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-car mr-1"></i>
                                    {th('invoiceApproval.detailModal.licensePlate')}:
                                </label>
                                <p className="mt-1">{mainItem.licensePlate || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-calendar mr-1"></i>
                                    {th('invoiceApproval.detailModal.createdDate')}:
                                </label>
                                <p className="mt-1">
                                    {mainItem.createdDate ? new Date(mainItem.createdDate).toLocaleDateString('tr-TR') : '-'}
                                </p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-calendar-check mr-1"></i>
                                    {th('invoiceApproval.detailModal.approvalDate')}:
                                </label>
                                <p className="mt-1">
                                    {mainItem.approvalDate ? new Date(mainItem.approvalDate).toLocaleDateString('tr-TR') : '-'}
                                </p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-shield mr-1"></i>
                                    {th('invoiceApproval.detailModal.warrantyEndDate')}:
                                </label>
                                <p className="mt-1">
                                    {mainItem.warrEndDate ? new Date(mainItem.warrEndDate).toLocaleDateString('tr-TR') : '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Branch Information */}
                    <div>
                        <h3 className="font-semibold text-lg mb-2">
                            <i className="pi pi-map-marker mr-2"></i>
                            {th('invoiceApproval.detailModal.branchInfo')}
                        </h3>
                        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-md">
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-code mr-1"></i>
                                    {th('invoiceApproval.detailModal.branchCode')}:
                                </label>
                                <p className="mt-1">{mainItem.bosBranchCode || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-map mr-1"></i>
                                    {th('invoiceApproval.detailModal.branchSiteCode')}:
                                </label>
                                <p className="mt-1">{mainItem.bosBranchSiteCode || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-bookmark mr-1"></i>
                                    {th('invoiceApproval.detailModal.allocation') + ' *'}:
                                </label>
                                <p className="mt-1">{mainItem.alokasyon || '-'}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-calendar mr-1"></i>
                                    {th('invoiceApproval.detailModal.failureDate')}:
                                </label>
                                <p className="mt-1">
                                    {mainItem.failureDate ? new Date(mainItem.failureDate).toLocaleDateString('tr-TR') : '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Parts Details Table */}
                    {parts.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-lg mb-2">
                                <i className="pi pi-cog mr-2"></i>
                                {th('invoiceApproval.detailModal.partsDetails')}
                            </h3>
                            <DataTable
                                value={parts}
                                className="p-datatable-sm"
                                showGridlines
                                stripedRows
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 20]}
                            >
                                <Column
                                    field="itemCode"
                                    header={th('invoiceApproval.detailModal.itemCode')}
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    field="itemCodeDesc"
                                    header={th('invoiceApproval.detailModal.itemDescription')}
                                    style={{ minWidth: '200px' }}
                                />
                                <Column
                                    field="quantity"
                                    header={th('invoiceApproval.detailModal.quantity')}
                                    style={{ minWidth: '100px', textAlign: 'right' }}
                                    body={(rowData) => rowData.quantity?.toLocaleString('tr-TR') || '0'}
                                />
                                <Column
                                    field="acceptedAmountPart"
                                    header={th('invoiceApproval.detailModal.acceptedAmount')}
                                    body={(rowData) => formatCurrency(rowData.acceptedAmountPart, rowData.itemPriceCurr)}
                                    style={{ minWidth: '150px', textAlign: 'right' }}
                                />
                                <Column
                                    field="itemPriceCurr"
                                    header={th('invoiceApproval.detailModal.currency')}
                                    style={{ minWidth: '100px', textAlign: 'center' }}
                                    body={(rowData) => rowData.itemPriceCurr || 'TRY'}
                                />
                            </DataTable>
                        </div>
                    )}

                    {/* Labor Details Table */}
                    {labors.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-lg mb-2">
                                <i className="pi pi-users mr-2"></i>
                                {th('invoiceApproval.detailModal.laborDetails')}
                            </h3>
                            <DataTable
                                value={labors}
                                className="p-datatable-sm"
                                showGridlines
                                stripedRows
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 20]}
                            >
                                <Column
                                    field="laborCode"
                                    header={th('invoiceApproval.detailModal.laborCode')}
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    field="iscilikTanimi"
                                    header={th('invoiceApproval.detailModal.laborDescription')}
                                    style={{ minWidth: '200px' }}
                                />
                                <Column
                                    field="laborTime"
                                    header={th('invoiceApproval.detailModal.laborTime')}
                                    style={{ minWidth: '100px', textAlign: 'right' }}
                                    body={(rowData) => rowData.laborTime?.toLocaleString('tr-TR') || '0'}
                                />
                                <Column
                                    field="acceptedAmountLabor"
                                    header={th('invoiceApproval.detailModal.acceptedAmount')}
                                    body={(rowData) => formatCurrency(rowData.acceptedAmountLabor, rowData.laborCurrency)}
                                    style={{ minWidth: '150px', textAlign: 'right' }}
                                />
                                <Column
                                    field="laborCurrency"
                                    header={th('invoiceApproval.detailModal.currency')}
                                    style={{ minWidth: '100px', textAlign: 'center' }}
                                    body={(rowData) => rowData.laborCurrency || 'TRY'}
                                />
                            </DataTable>
                        </div>
                    )}

                    {/* Other Costs Details Table */}
                    {others.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-lg mb-2">
                                <i className="pi pi-wallet mr-2"></i>
                                {th('invoiceApproval.detailModal.otherCostsDetails')}
                            </h3>
                            <DataTable
                                value={others}
                                className="p-datatable-sm"
                                showGridlines
                                stripedRows
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 20]}
                            >
                                <Column
                                    field="digerMaliyetTipi"
                                    header={th('invoiceApproval.detailModal.costType')}
                                    style={{ minWidth: '200px' }}
                                />
                                <Column
                                    field="acceptedAmountOther"
                                    header={th('invoiceApproval.detailModal.acceptedAmount')}
                                    body={(rowData) => formatCurrency(rowData.acceptedAmountOther, rowData.otherCurrency)}
                                    style={{ minWidth: '150px', textAlign: 'right' }}
                                />
                                <Column
                                    field="otherCurrency"
                                    header={th('invoiceApproval.detailModal.currency')}
                                    style={{ minWidth: '100px', textAlign: 'center' }}
                                    body={(rowData) => rowData.otherCurrency || 'TRY'}
                                />
                            </DataTable>
                        </div>
                    )}

                    {/* Summary Totals */}
                    <div>
                        <h3 className="font-semibold text-lg mb-2">
                            <i className="pi pi-chart-bar mr-2"></i>
                            {th('invoiceApproval.detailModal.summary')}
                        </h3>
                        <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-cog mr-1"></i>
                                    {th('invoiceApproval.detailModal.totalPartsAmount')}:
                                </label>
                                <p className="mt-1 font-semibold">{formatCurrency(totals.partsTotal, mainItem.currency)}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-users mr-1"></i>
                                    {th('invoiceApproval.detailModal.totalLaborAmount')}:
                                </label>
                                <p className="mt-1 font-semibold">{formatCurrency(totals.laborTotal, mainItem.currency)}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-wallet mr-1"></i>
                                    {th('invoiceApproval.detailModal.totalOtherCosts')}:
                                </label>
                                <p className="mt-1 font-semibold">{formatCurrency(totals.othersTotal, mainItem.currency)}</p>
                            </div>
                            <div>
                                <label className="font-medium">
                                    <i className="pi pi-calculator mr-1"></i>
                                    {th('invoiceApproval.detailModal.grandTotalClaimAmount')}:
                                </label>
                                <p className="mt-1 font-semibold text-blue-600">
                                    {formatCurrency(totals.totalClaimAmount, mainItem.currency)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Statistics */}
                    <div className="text-sm text-gray-500 mt-4 flex justify-between">
                        <span>
                            <i className="pi pi-list mr-1"></i>
                            {th('invoiceApproval.detailModal.partsCount').replace('{count}', parts.length)}
                        </span>
                        <span>
                            <i className="pi pi-briefcase mr-1"></i>
                            {th('invoiceApproval.detailModal.laborsCount').replace('{count}', labors.length)}
                        </span>
                        <span>
                            <i className="pi pi-credit-card mr-1"></i>
                            {th('invoiceApproval.detailModal.othersCount').replace('{count}', others.length)}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="text-center p-4">
                    <i className="pi pi-exclamation-triangle text-4xl text-gray-400 mb-3"></i>
                    <p>{th('invoiceApproval.detailModal.noDetailsFound')}</p>
                </div>
            )}
        </Dialog>
    );
};

export default InvoiceDetailModal;